import { useState } from 'react';
import { Link } from 'react-router-dom';

type SortKey = 'recent' | 'oldest' | 'alpha';

interface HeroSectionProps {
  total: number;
  activeCount: number;
  archivedCount: number;
  categoryCount: number;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  search: string;
  onSearch: (v: string) => void;
  authors: string[];
  activeAuthors: string[];
  onToggleAuthor: (a: string) => void;
  availableTags: string[];
  activeTags: string[];
  onToggleTag: (t: string) => void;
  activeFiltersCount: number;
  onClearAll: () => void;
}

export { type SortKey };

const SORT_OPTIONS: { key: SortKey; label: string; icon: string }[] = [
  { key: 'recent', label: 'Récent',  icon: 'schedule' },
  { key: 'oldest', label: 'Ancien',  icon: 'history'  },
  { key: 'alpha',  label: 'A → Z',   icon: 'sort_by_alpha' },
];

export default function HeroSection({
  total, activeCount, archivedCount, categoryCount,
  sort, onSort,
  search, onSearch,
  authors, activeAuthors, onToggleAuthor,
  availableTags, activeTags, onToggleTag,
  activeFiltersCount, onClearAll,
}: HeroSectionProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const hasAdvanced = authors.length > 0 || availableTags.length > 0;

  return (
    <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #1e3a5f 100%)', color: 'white' }}>

      {/* ── Title + stats ── */}
      <div style={{ padding: '32px 40px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <Link
            to="/"
            title="Retour à l'accueil Prototyper Studio (choix Size2Mix / Urban Map)"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              marginBottom: 8, padding: '4px 8px', borderRadius: 6,
              color: '#94a3b8', textDecoration: 'none', fontSize: 12, fontWeight: 500,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#e2e8f0'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>home</span>
            Accueil
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 24 }}>⚡</span>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px' }}>
              Nemo Prototypes Studio
            </h1>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
            Crée et partage des maquettes interactives avec le vrai système de design Nemo.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 28 }}>
          <StatBlock value={total}         label="Prototypes"  color="#e2e8f0" />
          <StatBlock value={activeCount}   label="Actifs"      color="#34d399" />
          <StatBlock value={categoryCount} label="Catégories"  color="#60a5fa" />
          <StatBlock value={archivedCount} label="Archivés"    color="#94a3b8" />
        </div>
      </div>

      {/* ── Search + sort + filters ── */}
      <div style={{ padding: '20px 40px 0', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>

        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <span className="material-symbols-outlined" style={{
            position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
            fontSize: 16, color: '#475569', pointerEvents: 'none', userSelect: 'none',
          }}>search</span>
          <input
            type="text"
            placeholder="Nom, description, tag, créateur…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '9px 32px 9px 34px',
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'white', fontSize: 13,
              outline: 'none', fontFamily: 'inherit',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.7)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
          />
          {search && (
            <button type="button" onClick={() => onSearch('')} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer',
              color: '#94a3b8', fontSize: 14, lineHeight: 1,
              width: 18, height: 18, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          )}
        </div>

        {/* Sort */}
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 3, gap: 2 }}>
          {SORT_OPTIONS.map(({ key, label, icon }) => {
            const active = sort === key;
            return (
              <button key={key} type="button" onClick={() => onSort(key)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 11px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit', border: 'none',
                background: active ? 'rgba(99,102,241,0.85)' : 'transparent',
                color: active ? 'white' : '#64748b',
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13, lineHeight: 1 }}>{icon}</span>
                {label}
              </button>
            );
          })}
        </div>

        {/* Advanced filters button */}
        {hasAdvanced && (
          <button type="button" onClick={() => setPanelOpen((v) => !v)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 13px', borderRadius: 8,
            border: `1px solid ${panelOpen || activeFiltersCount > 0 ? 'rgba(99,102,241,0.6)' : 'rgba(255,255,255,0.1)'}`,
            cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
            background: panelOpen || activeFiltersCount > 0 ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.06)',
            color: panelOpen || activeFiltersCount > 0 ? '#a5b4fc' : '#64748b',
            transition: 'all 0.15s',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 15, lineHeight: 1 }}>tune</span>
            Filtres
            {activeFiltersCount > 0 && (
              <span style={{
                background: '#6366f1', color: 'white',
                borderRadius: '50%', width: 17, height: 17,
                fontSize: 10, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{activeFiltersCount}</span>
            )}
          </button>
        )}

        {activeFiltersCount > 0 && (
          <button type="button" onClick={onClearAll} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#475569',
            fontSize: 12, fontFamily: 'inherit', padding: '4px 6px',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, lineHeight: 1 }}>close</span>
            Tout effacer
          </button>
        )}
      </div>

      {/* ── Advanced filter panel ── */}
      {panelOpen && hasAdvanced && (
        <div style={{
          margin: '16px 40px 0',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 12, padding: '18px 20px',
          display: 'flex', gap: 40, flexWrap: 'wrap',
        }}>
          {authors.length > 0 && (
            <FilterGroup icon="person" label="Créateur">
              {authors.map((a) => (
                <FilterChip key={a} label={`@${a.split('.')[0]}`} title={a}
                  active={activeAuthors.includes(a)} onClick={() => onToggleAuthor(a)} />
              ))}
            </FilterGroup>
          )}
          {availableTags.length > 0 && (
            <FilterGroup icon="label" label="Tag">
              {availableTags.map((t) => (
                <FilterChip key={t} label={t}
                  active={activeTags.includes(t)} onClick={() => onToggleTag(t)} />
              ))}
            </FilterGroup>
          )}
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}

function StatBlock({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: '#475569', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    </div>
  );
}

function FilterGroup({ icon, label, children }: { icon: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 13, color: '#475569', lineHeight: 1 }}>{icon}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.7px' }}>{label}</span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

function FilterChip({ label, title, active, onClick }: {
  label: string; title?: string; active: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} title={title} style={{
      padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500,
      cursor: 'pointer', fontFamily: 'inherit',
      border: `1px solid ${active ? '#6366f1' : 'rgba(255,255,255,0.1)'}`,
      background: active ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
      color: active ? '#a5b4fc' : '#94a3b8',
      transition: 'all 0.12s',
      display: 'inline-flex', alignItems: 'center', gap: active ? 5 : 0,
    }}>
      {active && <span className="material-symbols-outlined" style={{ fontSize: 12, lineHeight: 1 }}>check</span>}
      {label}
    </button>
  );
}
